/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { useTomraDrops } from "../../store/useTomraDrops";
import { useDropLocations } from "../../store/useDropLocations";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
  Tooltip,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import InformationIcon from "../../assets/icons/info/information.png";
import { PrimaryButton } from "../buttons/PrimaryButton";
import {
  getStoreColor,
  getCityColor,
  TOTAL_CONTRAST_COLOR,
} from "../../constants/plasticColors";

dayjs.extend(isoWeek);

interface StatieRevenueProps {
  filterType?: "location" | "city";
  filterPlaces?: string[];
}

export const StatieRevenue = React.memo(
  ({ filterType, filterPlaces }: StatieRevenueProps) => {
    const useGeneralFilter =
      filterType !== undefined && filterPlaces !== undefined;
    const snapshotRef = useRef(null);
    const { statieRevenuePerLocation, getStatieRevenuePerLocation } =
      useTomraDrops();
    const { dropLocations, getDropLocations } = useDropLocations();

    const [_, takeScreenshot] = useScreenshot();
    const [period, setPeriod] = useState<"month" | "week" | "quarter" | "year">(
      "month"
    );
    const [startYear, setStartYear] = useState(() => {
      const d = dayjs().subtract(12, "month");
      return d.year();
    });
    const [startMonth, setStartMonth] = useState(() => {
      const d = dayjs().subtract(12, "month");
      return d.month() + 1;
    });
    const [startWeek, setStartWeek] = useState(() => {
      const d = dayjs().subtract(12, "month");
      return d.isoWeek();
    });
    const [startQuarter, setStartQuarter] = useState(1);
    const [endYear, setEndYear] = useState(dayjs().year());
    const [endMonth, setEndMonth] = useState(dayjs().month() + 1);
    const [endWeek, setEndWeek] = useState(dayjs().isoWeek());
    const [endQuarter, setEndQuarter] = useState(4);

    const yearOptions = Array.from(
      { length: dayjs().year() - 2023 },
      (_, i) => dayjs().year() - i
    );

    const monthOptions = [
      { value: 1, label: "Jan" },
      { value: 2, label: "Feb" },
      { value: 3, label: "Mar" },
      { value: 4, label: "Apr" },
      { value: 5, label: "May" },
      { value: 6, label: "Jun" },
      { value: 7, label: "Jul" },
      { value: 8, label: "Aug" },
      { value: 9, label: "Sep" },
      { value: 10, label: "Oct" },
      { value: 11, label: "Nov" },
      { value: 12, label: "Dec" },
    ];

    const quarterOptions = [
      { value: 1, label: "Q1" },
      { value: 2, label: "Q2" },
      { value: 3, label: "Q3" },
      { value: 4, label: "Q4" },
    ];

    const getWeekOptions = (year: number) => {
      const lastDay = dayjs(`${year}-12-31`);
      const totalWeeks = lastDay.isoWeek() === 1 ? 52 : lastDay.isoWeek();
      const currentWeek = dayjs().isoWeek();
      const currentYear = dayjs().year();
      return Array.from({ length: totalWeeks }, (_, i) => {
        const w = i + 1;
        const start = dayjs().year(year).isoWeek(w).startOf("isoWeek");
        const end = dayjs().year(year).isoWeek(w).endOf("isoWeek");
        return {
          value: w,
          label: `Week ${w}: ${start.format("MMM DD")} - ${end.format("MMM DD")}`,
          disabled: year === currentYear && w > currentWeek,
        };
      });
    };

    const startMonthOptions = useMemo(() => {
      const cy = dayjs().year();
      const cm = dayjs().month() + 1;
      return startYear === cy
        ? monthOptions.filter((m) => m.value <= cm)
        : monthOptions;
    }, [startYear]);

    const endMonthOptions = useMemo(() => {
      const cy = dayjs().year();
      const cm = dayjs().month() + 1;
      return endYear === cy
        ? monthOptions.filter((m) => m.value <= cm)
        : monthOptions;
    }, [endYear]);

    const startWeekOptions = useMemo(
      () => getWeekOptions(startYear),
      [startYear]
    );
    const endWeekOptions = useMemo(() => getWeekOptions(endYear), [endYear]);

    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        const link = document.createElement("a");
        link.href = image;
        link.download = "statie_revenue.png";
        link.click();
      });
    };

    const TOTAL_ALL_STORES_ID = "__TOTAL_ALL_STORES__";

    const filteredStatieRevenueData = useMemo(() => {
      if (!useGeneralFilter) return statieRevenuePerLocation ?? [];
      const raw = statieRevenuePerLocation ?? [];
      const places = filterPlaces ?? [];
      const includeTotal = places.includes(TOTAL_ALL_STORES_ID);
      const realPlaces = places.filter((p) => p !== TOTAL_ALL_STORES_ID);

      if (!places.length) return raw;

      if (!realPlaces.length) {
        if (!includeTotal) return [];
        const base = raw;
        if (!base.length) return [];
        const firstData =
          (base[0]?.data as [number, number][] | undefined) ?? [];
        const totalData = firstData.map(([x], idx) => {
          const sum = base.reduce((acc, s) => {
            const d = (s?.data as [number, number][] | undefined) ?? [];
            const val = d[idx]?.[1] ?? 0;
            return acc + (Number(val) || 0);
          }, 0);
          return [x, sum] as [number, number];
        });
        return [{ name: "Total all stores", data: totalData }];
      }

      const selectedNames = new Set<string>();
      if ((filterType ?? "location") === "city") {
        realPlaces.forEach((p) => selectedNames.add(p));
      } else {
        realPlaces.forEach((id) => {
          const loc = dropLocations?.find((l) => l._id === id);
          if (loc?.cost_place) {
            selectedNames.add(loc.cost_place as string);
          }
        });
      }
      let filtered = raw.filter((s) => selectedNames.has(s.name));

      if (includeTotal && filtered.length > 0) {
        const firstData =
          (filtered[0]?.data as [number, number][] | undefined) ?? [];
        const totalData = firstData.map(([x], idx) => {
          const sum = filtered.reduce((acc, s) => {
            const d = (s?.data as [number, number][] | undefined) ?? [];
            const val = d[idx]?.[1] ?? 0;
            return acc + (Number(val) || 0);
          }, 0);
          return [x, sum] as [number, number];
        });
        filtered = [...filtered, { name: "Total all stores", data: totalData }];
      }
      return filtered;
    }, [
      statieRevenuePerLocation,
      useGeneralFilter,
      filterType,
      filterPlaces,
      dropLocations,
    ]);

    const seriesNames = useMemo(
      () => (filteredStatieRevenueData ?? []).map((s) => s?.name || ""),
      [filteredStatieRevenueData]
    );

    const seriesColors = useMemo(() => {
      const isCityView =
        useGeneralFilter && (filterType ?? "location") === "city";
      return seriesNames.map((name) =>
        name === "Total all stores"
          ? TOTAL_CONTRAST_COLOR
          : isCityView
            ? getCityColor(name)
            : getStoreColor(name)
      );
    }, [seriesNames, useGeneralFilter, filterType]);

    const { chartData, categoryLabels } = useMemo(() => {
      if (!filteredStatieRevenueData?.length) {
        return { chartData: [], categoryLabels: [] };
      }
      const firstSeries = filteredStatieRevenueData[0];
      const rawData = firstSeries?.data;
      if (!rawData || !Array.isArray(rawData)) {
        return { chartData: [], categoryLabels: [] };
      }
      const rawDataTyped = rawData as [number, number][];

      // Yearly: aggregate by year
      if (period === "year") {
        const yearSet = new Set<number>();
        rawDataTyped.forEach(([ts]) => {
          yearSet.add(dayjs(ts).year());
        });
        const years = Array.from(yearSet).sort((a, b) => a - b);

        const chartData = filteredStatieRevenueData.map((s) => {
          const sData = s.data as [number, number][] | undefined;
          const yearSums = new Map<number, number>();
          sData?.forEach(([ts, val]) => {
            const y = dayjs(ts).year();
            yearSums.set(y, (yearSums.get(y) ?? 0) + val);
          });
          return {
            name: s.name,
            data: years.map((y) => yearSums.get(y) ?? 0),
          };
        });

        const categoryLabels = years.map((y) => y.toString());
        return { chartData, categoryLabels };
      }

      // Quarterly: aggregate by quarter
      if (period === "quarter") {
        const buckets = new Map<string, number>();
        const labelsSet = new Set<string>();

        rawDataTyped.forEach(([ts]) => {
          const d = dayjs(ts);
          const q = Math.ceil((d.month() + 1) / 3);
          const key = `${d.year()}-Q${q}`;
          labelsSet.add(key);
        });
        const labels = Array.from(labelsSet).sort();

        const chartData = filteredStatieRevenueData.map((s) => {
          const sData = s.data as [number, number][] | undefined;
          const sums = new Map<string, number>();
          sData?.forEach(([ts, val]) => {
            const d = dayjs(ts);
            const q = Math.ceil((d.month() + 1) / 3);
            const key = `${d.year()}-Q${q}`;
            sums.set(key, (sums.get(key) ?? 0) + val);
          });
          return {
            name: s.name,
            data: labels.map((key) => sums.get(key) ?? 0),
          };
        });

        return { chartData, categoryLabels: labels };
      }

      const sortedTs = Array.from(new Set(rawDataTyped.map((d) => d[0]))).sort(
        (a, b) => a - b
      );
      const categoryLabels = sortedTs.map((ts) => {
        if (period === "week") {
          return `${dayjs(ts).year()}-W${String(dayjs(ts).isoWeek()).padStart(2, "0")}`;
        }
        return dayjs(ts).format("MMM YYYY");
      });
      const chartData = filteredStatieRevenueData.map((s) => {
        const sData = s.data as [number, number][] | undefined;
        return {
          name: s.name,
          data: sortedTs.map((ts) => {
            const pair = sData?.find((d) => d[0] === ts);
            return pair ? pair[1] : 0;
          }),
        };
      });
      return { chartData, categoryLabels };
    }, [filteredStatieRevenueData, period]);

    useEffect(() => {
      getDropLocations();
    }, []);

    useEffect(() => {
      const apiPeriod: "monthly" | "weekly" =
        period === "week" ? "weekly" : "monthly";

      let startValue: number;
      let endValue: number;

      if (apiPeriod === "weekly") {
        startValue = startWeek;
        endValue = endWeek;
      } else {
        if (period === "month") {
          startValue = startMonth;
          endValue = endMonth;
        } else if (period === "quarter") {
          startValue = (startQuarter - 1) * 3 + 1;
          endValue = endQuarter * 3;
        } else if (period === "year") {
          startValue = 1;
          endValue = 12;
        } else {
          startValue = startMonth;
          endValue = endMonth;
        }
      }

      getStatieRevenuePerLocation(
        startYear,
        startValue,
        endYear,
        endValue,
        apiPeriod,
        useGeneralFilter ? (filterType ?? "location") : undefined,
        undefined
      );
    }, [
      period,
      startYear,
      startMonth,
      startWeek,
      startQuarter,
      endYear,
      endMonth,
      endWeek,
      endQuarter,
      useGeneralFilter,
      filterType,
      getStatieRevenuePerLocation,
    ]);

    const periodLabel =
      period === "month"
        ? "€/month"
        : period === "week"
          ? "€/week"
          : period === "quarter"
            ? "€/quarter"
            : "€/year";

    return (
      <div
        ref={snapshotRef}
        className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl shadow-none mb-1 sm:mb-10"
      >
        <div className="flex flex-col sm:flex-row w-full items-start justify-between">
          <div className="flex items-center mb-2 sm:mb-12">
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
              Statie Revenue
            </p>
            <Tooltip content="Statie revenue (refunds + machine revenue). Non-cumulative.">
              <img
                src={InformationIcon}
                alt="info"
                className="inline-block w-4 h-4 ml-2 cursor-pointer"
              />
            </Tooltip>
          </div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-center justify-start sm:justify-end gap-2 sm:gap-3">
            <div className="flex items-center gap-1 sm:gap-2">
              <label className="font-satoshi text-xs sm:text-sm text-gray-text whitespace-nowrap">
                Period:
              </label>
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                    {period === "month"
                      ? "Monthly"
                      : period === "week"
                        ? "Weekly"
                        : period === "quarter"
                          ? "Quarterly"
                          : "Yearly"}
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  <MenuItem
                    disabled={period === "month"}
                    onClick={() => setPeriod("month")}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    Monthly
                  </MenuItem>
                  <MenuItem
                    disabled={period === "week"}
                    onClick={() => setPeriod("week")}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    Weekly
                  </MenuItem>
                  <MenuItem
                    disabled={period === "quarter"}
                    onClick={() => setPeriod("quarter")}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    Quarterly
                  </MenuItem>
                  <MenuItem
                    disabled={period === "year"}
                    onClick={() => setPeriod("year")}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    Yearly
                  </MenuItem>
                </MenuList>
              </Menu>
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              <label className="font-satoshi text-xs sm:text-sm text-gray-text whitespace-nowrap">
                From:
              </label>
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                    {startYear}
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {yearOptions.map((y) => (
                    <MenuItem
                      key={y}
                      disabled={startYear === y}
                      onClick={() => setStartYear(y)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {y}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
              {period === "month" && (
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                      {startMonthOptions.find((m) => m.value === startMonth)
                        ?.label || "Month"}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {startMonthOptions.map((m) => (
                      <MenuItem
                        key={m.value}
                        disabled={startMonth === m.value}
                        onClick={() => setStartMonth(m.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {m.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
              {period === "week" && (
                <Menu>
                  <MenuHandler>
                    <button
                      className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                      title={
                        startWeekOptions.find((w) => w.value === startWeek)
                          ?.label
                      }
                    >
                      {startWeekOptions.find((w) => w.value === startWeek)
                        ?.label || "Week"}
                      <ChevronDown className="ml-1 sm:ml-2 flex-shrink-0" />
                    </button>
                  </MenuHandler>
                  <MenuList
                    className="rounded-none max-h-[250px] overflow-y-auto"
                    placeholder={undefined}
                  >
                    {startWeekOptions.map((w) => (
                      <MenuItem
                        key={w.value}
                        disabled={w.disabled || startWeek === w.value}
                        onClick={() => setStartWeek(w.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {w.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
              {period === "quarter" && (
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                      {quarterOptions.find((q) => q.value === startQuarter)
                        ?.label || "Q1"}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {quarterOptions.map((q) => (
                      <MenuItem
                        key={q.value}
                        disabled={startQuarter === q.value}
                        onClick={() => setStartQuarter(q.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {q.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              <label className="font-satoshi text-xs sm:text-sm text-gray-text whitespace-nowrap">
                To:
              </label>
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                    {endYear}
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {yearOptions.map((y) => (
                    <MenuItem
                      key={y}
                      disabled={endYear === y}
                      onClick={() => setEndYear(y)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {y}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
              {period === "month" && (
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                      {endMonthOptions.find((m) => m.value === endMonth)
                        ?.label || "Month"}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {endMonthOptions.map((m) => (
                      <MenuItem
                        key={m.value}
                        disabled={endMonth === m.value}
                        onClick={() => setEndMonth(m.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {m.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
              {period === "week" && (
                <Menu>
                  <MenuHandler>
                    <button
                      className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                      title={
                        endWeekOptions.find((w) => w.value === endWeek)?.label
                      }
                    >
                      {endWeekOptions.find((w) => w.value === endWeek)?.label ||
                        "Week"}
                      <ChevronDown className="ml-1 sm:ml-2 flex-shrink-0" />
                    </button>
                  </MenuHandler>
                  <MenuList
                    className="rounded-none max-h-[250px] overflow-y-auto"
                    placeholder={undefined}
                  >
                    {endWeekOptions.map((w) => (
                      <MenuItem
                        key={w.value}
                        disabled={w.disabled || endWeek === w.value}
                        onClick={() => setEndWeek(w.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {w.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
              {period === "quarter" && (
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                      {quarterOptions.find((q) => q.value === endQuarter)
                        ?.label || "Q4"}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {quarterOptions.map((q) => (
                      <MenuItem
                        key={q.value}
                        disabled={endQuarter === q.value}
                        onClick={() => setEndQuarter(q.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {q.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
            </div>

            <PrimaryButton
              size="sm"
              onClick={downloadScreenshot}
              label="Download image"
              className="!bg-droppie-oranje !border-droppie-oranje"
            />
          </div>
        </div>
        <div>
          {chartData.length > 0 && categoryLabels.length > 0 ? (
            <Chart
              type="line"
              options={{
                ...options,
                colors: seriesColors.length > 0 ? seriesColors : options.colors,
                xaxis: {
                  categories: categoryLabels,
                  title: {
                    text:
                      period === "week"
                        ? "Week"
                        : period === "quarter"
                          ? "Quarter"
                          : period === "year"
                            ? "Year"
                            : "Month",
                    style: { fontSize: "14px", fontWeight: "bold" },
                  },
                  labels: {
                    style: {
                      fontSize: "12px",
                      fontFamily: "Satoshi, sans-serif",
                      colors: "#374151",
                    },
                  },
                },
              }}
              series={chartData}
              height={400}
            />
          ) : (
            <div className="flex items-center justify-center h-[300px] w-full">
              <svg
                aria-hidden="true"
                className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-primary-base"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
            </div>
          )}
        </div>
      </div>
    );
  }
);

const options = {
  chart: {
    type: "line" as const,
    zoom: { enabled: false },
    toolbar: { show: false },
  },
  stroke: { width: 2 },
  dataLabels: { enabled: false },
  grid: { strokeDashArray: 5 },
  markers: { size: 1 },
  legend: { show: false },
  tooltip: {
    shared: true,
    intersect: false,
  },
  yaxis: {
    title: {
      text: "Statie Revenue (€)",
      style: { fontSize: "14px", fontWeight: "bold" },
    },
    labels: {
      formatter: function (value: number) {
        return new Intl.NumberFormat("nl-NL", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(value ?? 0));
      },
    },
  },
  colors: [
    "#e34e76",
    "#007887",
    "#8773aa",
    "#e6e1ff",
    "#fe3e3e",
    "#ffff78",
    "#d2e6ff",
    "#d2d2b2",
    "#5fd0ae",
    "#ff8dfa",
    "#ff6e41",
    "#808080",
  ],
};
